import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Analytics Settings Page Component
 *
 * Configure third-party analytics integrations (GA4, Meta Pixel, Mixpanel)
 * Uses inline styles for WordPress admin compatibility
 *
 * @package WooAIChatbot
 */
import { useState, useEffect, useCallback } from 'react';
import { Settings, Save, Loader2, CheckCircle, AlertCircle, BarChart3 } from 'lucide-react';
import { Card } from '../../components/ui/card';
import { useTranslation } from '../../hooks/useTranslation';
import apiClient from '../../utils/api/client';
/**
 * Default settings values
 */
const defaultSettings = {
    ga4_enabled: false,
    ga4_measurement_id: '',
    meta_pixel_enabled: false,
    meta_pixel_id: '',
    mixpanel_enabled: false,
    mixpanel_token: '',
};
/**
 * Inline styles for WordPress compatibility
 */
const styles = {
    page: {
        padding: '32px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 500,
        color: '#111827',
        margin: 0,
    },
    subtitle: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0,
    },
    saveButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '40px',
        padding: '0 20px',
        borderRadius: '10px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    saveButtonDisabled: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        height: '40px',
        padding: '0 20px',
        borderRadius: '10px',
        border: 'none',
        backgroundColor: '#d1d5db',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'not-allowed',
    },
    settingsCard: {
        backgroundColor: '#ffffff',
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.1)',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
    },
    providerSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        paddingBottom: '24px',
        borderBottom: '1px solid #e5e7eb',
    },
    providerSectionLast: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        paddingBottom: '0',
        borderBottom: 'none',
    },
    providerHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    providerInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
    },
    providerIcon: {
        width: '40px',
        height: '40px',
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '20px',
    },
    providerDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    providerName: {
        fontSize: '16px',
        fontWeight: 500,
        color: '#111827',
        margin: 0,
    },
    providerDescription: {
        fontSize: '14px',
        color: '#6b7280',
        margin: 0,
    },
    toggle: {
        position: 'relative',
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        border: 'none',
        cursor: 'pointer',
        transition: 'background 0.2s ease',
        padding: 0,
        flexShrink: 0,
    },
    toggleKnob: {
        position: 'absolute',
        top: '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: 'white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 0.2s ease',
    },
    inputGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    label: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#374151',
    },
    input: {
        width: '100%',
        height: '40px',
        padding: '0 12px',
        borderRadius: '8px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
    },
    inputDisabled: {
        width: '100%',
        height: '40px',
        padding: '0 12px',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        fontSize: '14px',
        boxSizing: 'border-box',
        backgroundColor: '#f9fafb',
        color: '#9ca3af',
        cursor: 'not-allowed',
    },
    hint: {
        fontSize: '12px',
        color: '#6b7280',
        margin: 0,
    },
    toast: {
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px 16px',
        borderRadius: '10px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 9999,
        animation: 'slideIn 0.3s ease',
    },
    toastSuccess: {
        backgroundColor: '#10b981',
        color: '#ffffff',
    },
    toastError: {
        backgroundColor: '#ef4444',
        color: '#ffffff',
    },
    loading: {
        padding: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
    },
    loadingContent: {
        textAlign: 'center',
    },
    spinner: {
        width: '32px',
        height: '32px',
        margin: '0 auto 8px',
        color: '#6366f1',
    },
    errorCard: {
        padding: '24px',
        borderRadius: '14px',
        border: '1px solid #ef4444',
        backgroundColor: '#fef2f2',
    },
    errorTitle: {
        fontWeight: 600,
        color: '#ef4444',
        marginBottom: '8px',
    },
    errorMessage: {
        fontSize: '14px',
        color: '#374151',
        marginBottom: '16px',
    },
    retryButton: {
        padding: '8px 16px',
        borderRadius: '10px',
        border: 'none',
        backgroundColor: '#6366f1',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
};
/**
 * Analytics Settings Component
 */
export function AnalyticsSettings() {
    const { t } = useTranslation();
    const [settings, setSettings] = useState(defaultSettings);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);
    /**
     * Load settings from API
     */
    const loadSettings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await apiClient.get('analytics/settings');
            setSettings({ ...defaultSettings, ...data });
        }
        catch (err) {
            console.error('Failed to load analytics settings:', err);
            setError(err instanceof Error ? err : new Error('Failed to load settings'));
        }
        finally {
            setLoading(false);
        }
    }, []);
    /**
     * Save settings to API
     */
    const saveSettings = async () => {
        setSaving(true);
        try {
            await apiClient.put('analytics/settings', settings);
            showToast('success', 'Settings saved successfully');
        }
        catch (err) {
            console.error('Failed to save analytics settings:', err);
            showToast('error', 'Failed to save settings. Please try again.');
        }
        finally {
            setSaving(false);
        }
    };
    /**
     * Show toast notification
     */
    const showToast = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3000);
    };
    /**
     * Handle toggle change
     */
    const handleToggle = (field) => {
        setSettings(prev => ({
            ...prev,
            [field]: !prev[field],
        }));
    };
    /**
     * Handle input change
     */
    const handleInputChange = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value,
        }));
    };
    /**
     * Load settings on mount
     */
    useEffect(() => {
        loadSettings();
    }, [loadSettings]);
    /**
     * Render loading state
     */
    if (loading) {
        return (_jsx("div", { style: styles.loading, children: _jsxs("div", { style: styles.loadingContent, children: [_jsx(Loader2, { style: { ...styles.spinner, animation: 'spin 1s linear infinite' } }), _jsx("p", { style: { color: '#6b7280' }, children: "Loading analytics settings..." })] }) }));
    }
    /**
     * Render error state
     */
    if (error) {
        return (_jsx("div", { style: styles.page, children: _jsxs("div", { style: styles.errorCard, children: [_jsx("h3", { style: styles.errorTitle, children: "Error Loading Settings" }), _jsx("p", { style: styles.errorMessage, children: error.message }), _jsx("button", { onClick: loadSettings, style: styles.retryButton, children: t('retry') })] }) }));
    }
    return (_jsxs("div", { style: styles.page, children: [_jsxs("div", { style: styles.header, children: [_jsxs("div", { style: styles.headerContent, children: [_jsx("h1", { style: styles.title, children: t('analyticsIntegrations') }), _jsx("p", { style: styles.subtitle, children: t('analyticsIntegrationsDescription') })] }), _jsx("button", { onClick: saveSettings, disabled: saving, style: saving ? styles.saveButtonDisabled : styles.saveButton, children: saving ? (_jsxs(_Fragment, { children: [_jsx(Loader2, { style: { width: '16px', height: '16px', animation: 'spin 1s linear infinite' } }), t('saving')] })) : (_jsxs(_Fragment, { children: [_jsx(Save, { style: { width: '16px', height: '16px' } }), t('saveSettings')] })) })] }), _jsxs(Card, { style: styles.settingsCard, children: [_jsxs("div", { style: styles.providerSection, children: [_jsxs("div", { style: styles.providerHeader, children: [_jsxs("div", { style: styles.providerInfo, children: [_jsx("div", { style: { ...styles.providerIcon, backgroundColor: '#fff3cd' }, children: _jsx(BarChart3, { style: { width: '20px', height: '20px', color: '#f59e0b' } }) }), _jsxs("div", { style: styles.providerDetails, children: [_jsx("h3", { style: styles.providerName, children: t('googleAnalytics4') }), _jsx("p", { style: styles.providerDescription, children: t('ga4Description') })] })] }), _jsx("button", { onClick: () => handleToggle('ga4_enabled'), style: {
                                            ...styles.toggle,
                                            background: settings.ga4_enabled ? '#6366f1' : '#d1d5db',
                                        }, title: settings.ga4_enabled ? 'Click to disable' : 'Click to enable', children: _jsx("span", { style: {
                                                ...styles.toggleKnob,
                                                left: settings.ga4_enabled ? '22px' : '2px',
                                            } }) })] }), _jsxs("div", { style: styles.inputGroup, children: [_jsx("label", { style: styles.label, children: t('measurementId') }), _jsx("input", { type: "text", value: settings.ga4_measurement_id, onChange: (e) => handleInputChange('ga4_measurement_id', e.target.value), placeholder: "G-XXXXXXXXXX", disabled: !settings.ga4_enabled, style: settings.ga4_enabled ? styles.input : styles.inputDisabled }), _jsx("p", { style: styles.hint, children: "Find your Measurement ID in Google Analytics Admin > Data Streams" })] })] }), _jsxs("div", { style: styles.providerSection, children: [_jsxs("div", { style: styles.providerHeader, children: [_jsxs("div", { style: styles.providerInfo, children: [_jsx("div", { style: { ...styles.providerIcon, backgroundColor: '#e0e7ff' }, children: _jsx(Settings, { style: { width: '20px', height: '20px', color: '#4f46e5' } }) }), _jsxs("div", { style: styles.providerDetails, children: [_jsx("h3", { style: styles.providerName, children: t('metaPixel') }), _jsx("p", { style: styles.providerDescription, children: t('metaPixelDescription') })] })] }), _jsx("button", { onClick: () => handleToggle('meta_pixel_enabled'), style: {
                                            ...styles.toggle,
                                            background: settings.meta_pixel_enabled ? '#6366f1' : '#d1d5db',
                                        }, title: settings.meta_pixel_enabled ? 'Click to disable' : 'Click to enable', children: _jsx("span", { style: {
                                                ...styles.toggleKnob,
                                                left: settings.meta_pixel_enabled ? '22px' : '2px',
                                            } }) })] }), _jsxs("div", { style: styles.inputGroup, children: [_jsx("label", { style: styles.label, children: t('pixelId') }), _jsx("input", { type: "text", value: settings.meta_pixel_id, onChange: (e) => handleInputChange('meta_pixel_id', e.target.value), placeholder: "1234567890123456", disabled: !settings.meta_pixel_enabled, style: settings.meta_pixel_enabled ? styles.input : styles.inputDisabled }), _jsx("p", { style: styles.hint, children: "Find your Pixel ID in Meta Events Manager" })] })] }), _jsxs("div", { style: styles.providerSectionLast, children: [_jsxs("div", { style: styles.providerHeader, children: [_jsxs("div", { style: styles.providerInfo, children: [_jsx("div", { style: { ...styles.providerIcon, backgroundColor: '#fce7f3' }, children: _jsx(BarChart3, { style: { width: '20px', height: '20px', color: '#ec4899' } }) }), _jsxs("div", { style: styles.providerDetails, children: [_jsx("h3", { style: styles.providerName, children: t('mixpanel') }), _jsx("p", { style: styles.providerDescription, children: t('mixpanelDescription') })] })] }), _jsx("button", { onClick: () => handleToggle('mixpanel_enabled'), style: {
                                            ...styles.toggle,
                                            background: settings.mixpanel_enabled ? '#6366f1' : '#d1d5db',
                                        }, title: settings.mixpanel_enabled ? 'Click to disable' : 'Click to enable', children: _jsx("span", { style: {
                                                ...styles.toggleKnob,
                                                left: settings.mixpanel_enabled ? '22px' : '2px',
                                            } }) })] }), _jsxs("div", { style: styles.inputGroup, children: [_jsx("label", { style: styles.label, children: t('projectToken') }), _jsx("input", { type: "text", value: settings.mixpanel_token, onChange: (e) => handleInputChange('mixpanel_token', e.target.value), placeholder: "abc123def456ghi789", disabled: !settings.mixpanel_enabled, style: settings.mixpanel_enabled ? styles.input : styles.inputDisabled }), _jsx("p", { style: styles.hint, children: "Find your Project Token in Mixpanel Settings > Project Settings" })] })] })] }), toast && (_jsxs("div", { style: {
                    ...styles.toast,
                    ...(toast.type === 'success' ? styles.toastSuccess : styles.toastError),
                }, children: [toast.type === 'success' ? (_jsx(CheckCircle, { style: { width: '16px', height: '16px' } })) : (_jsx(AlertCircle, { style: { width: '16px', height: '16px' } })), toast.message] })), _jsx("style", { children: `
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      ` })] }));
}
